//Proofed
import { Box, Container, Grid, Typography, Stack, Chip, Card, CardContent } from '@mui/material'
import { Link } from 'react-router-dom'
import { FancyGoldButton } from '../../components/FancyGoldButton'
import { FancyLightButton } from '../../components/FancyLightButton'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import FormatQuoteIcon from '@mui/icons-material/FormatQuote'
import PublicIcon from '@mui/icons-material/Public'
import { DARK_800, NAVBAR_BG, BG_LIGHT, SOLUTIONS_HERO_START, SOLUTIONS_HERO_MID, COLOR_PRIMARY } from '../../theme'
import { WaveDivider } from '../../components/WaveDivider'
import { CtaSection } from '../../components/CtaSection'
import PageMeta from '../../components/PageMeta'
import { BookDemoModal } from '../../components/BookDemoModal'
import { useState } from 'react'
import { ACCENT, journey, challenges, heroOutcomes, qualifierCards } from '../../common/NetworkGlobalOpportunitiesContent'
import { ChallengesSection } from '../../components/ChallengesSection'

const SECTION_DARK = {
    backgroundColor: DARK_800,
    py: { xs: 6, md: 10 },
}

export default function GlobalOpportunities() {
    const [demoOpen, setDemoOpen] = useState(false)

    return (
        <Box>
            <PageMeta
                title="Global Opportunities"
                description="How Contract Finder Pro helped an overseas business win its first UK public sector contracts. A case study in market entry, bid writing and building a sustainable public sector pipeline."
                canonical="/success-network/global-opportunities"
            />
            {/* ── Hero ─────────────────────────────────────────────────────────── */}
            <Box sx={{
                background: `linear-gradient(110deg, white 0%, ${ACCENT}15 55%, ${ACCENT}30 100%)`,
                pt: { xs: 6, md: 10 },
                pb: { xs: 6, md: 10 },
            }}>
                <Container maxWidth="lg">
                    <Grid container spacing={5} alignItems="stretch">
                        {/* Left  copy */}
                        <Grid item xs={12} md={6}>
                            <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
                                <Chip label="Case Study" size="small" sx={{
                                    backgroundColor: `${ACCENT}12`, color: ACCENT,
                                    border: `1px solid ${ACCENT}40`, fontWeight: 700,
                                }} />
                                <Chip label="Global Opportunities" size="small" sx={{
                                    backgroundColor: `${COLOR_PRIMARY}20`, color: '#856400',
                                    border: `1px solid ${COLOR_PRIMARY}60`, fontWeight: 700,
                                }} />
                            </Stack>
                            <Typography variant="h1" sx={{ fontSize: { xs: '34px', md: '50px' }, mb: 2, color: NAVBAR_BG, lineHeight: 1.15 }}>
                                From Vietnam to{' '}
                                <Box component="span" sx={{ color: ACCENT }}>UK Government Contracts</Box>
                            </Typography>
                            <Typography variant="h5" sx={{ color: 'text.secondary', fontWeight: 400, mb: 4, lineHeight: 1.7, fontSize: { xs: '16px', md: '18px' } }}>
                                How Contract Finder Pro guided a Vietnamese manufacturer through UK market entry, framework qualification and active tender submissions in the UK public sector
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <FancyGoldButton onClick={() => setDemoOpen(true)} endIcon={<ArrowForwardIcon />}>
                                    Book a Discovery Call
                                </FancyGoldButton>
                                <FancyLightButton to="/contactus">
                                    Contact Us
                                </FancyLightButton>
                            </Stack>
                        </Grid>

                        {/* Right  dark context panel */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                background: `linear-gradient(135deg, ${SOLUTIONS_HERO_START} 0%, ${SOLUTIONS_HERO_MID} 100%)`,
                                border: `1px solid ${ACCENT}30`,
                                borderRadius: 3,
                                p: { xs: 3, md: 4 },
                                height: '100%',
                            }}>
                                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                                    <PublicIcon sx={{ color: ACCENT, fontSize: 20 }} />
                                    <Typography variant="caption" sx={{
                                        color: ACCENT, textTransform: 'uppercase',
                                        letterSpacing: '1px', fontWeight: 700,
                                    }}>
                                        The opportunity
                                    </Typography>
                                </Stack>
                                <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '15px', lineHeight: 1.8, mb: 3 }}>
                                    The UK government spends over <Box component="span" sx={{ color: COLOR_PRIMARY, fontWeight: 700 }}>£300 billion annually</Box> through public procurement from textiles and garments to technology, logistics and professional services. For Vietnamese manufacturers, this represents a significant and largely untapped opportunity
                                </Typography>
                                <Box sx={{ borderTop: `1px solid rgba(255,255,255,0.1)`, pt: 3 }}>
                                    <Typography variant="caption" sx={{
                                        color: ACCENT, textTransform: 'uppercase',
                                        letterSpacing: '1px', fontWeight: 700, display: 'block', mb: 2,
                                    }}>
                                        This engagement delivered
                                    </Typography>
                                    <Stack spacing={1.5}>
                                        {heroOutcomes.map(h => (
                                            <Box key={h} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                                <CheckCircleIcon sx={{ color: COLOR_PRIMARY, fontSize: 16, mt: 0.2, flexShrink: 0 }} />
                                                <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', lineHeight: 1.6 }}>{h}</Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <WaveDivider top={`linear-gradient(110deg, white 0%, ${ACCENT}15 55%, ${ACCENT}30 100%)`} bottom={DARK_800} />

            {/* ── The challenges ───────────────────────────────────────────────── */}
            <ChallengesSection
                title="The starting point, three real barriers to entry"
                subtitle="Every overseas supplier entering the UK public sector faces the same blockers. Recognising them early is what makes the difference between a wasted year and a first contract win"
                items={challenges}
                color={ACCENT}
            />
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />
            
            {/* ── The journey ──────────────────────────────────────────────────── */}
            <Box sx={{ backgroundColor: 'white', py: { xs: 6, md: 10 } }}>
                <Container maxWidth="lg">
                    <Typography variant="h2" sx={{ textAlign: 'center', mb: 2 }}>
                        How we structured the engagement
                    </Typography>
                    <Typography color="text.secondary" sx={{ textAlign: 'center', mb: 8, maxWidth: 560, mx: 'auto' }}>
                        Three phases, each building on the last from understanding the landscape to active contract pursuit
                    </Typography>
                    <Grid container spacing={4}>
                        {journey.map((item, i) => (
                            <Grid item xs={12} md={4} key={item.title}>
                                <Card variant="outlined" sx={{
                                    height: '100%',
                                    borderColor: `${ACCENT}25`,
                                    transition: 'all 0.2s',
                                    '&:hover': { borderColor: ACCENT, boxShadow: `0 4px 24px ${ACCENT}20` },
                                }}>
                                    <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2.5 }}>
                                            <Box sx={{
                                                width: 48, height: 48, borderRadius: 2,
                                                backgroundColor: `${ACCENT}12`,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                flexShrink: 0,
                                            }}>
                                                <item.Icon sx={{ color: ACCENT, fontSize: 36 }} />
                                            </Box>
                                            <Typography sx={{ color: `${ACCENT}58`, fontWeight: 800, fontSize: '40px', lineHeight: 1 }}>
                                                {item.step}
                                            </Typography>
                                        </Box>
                                        <Typography fontWeight={700} sx={{ fontSize: '16px', mb: 1.5, lineHeight: 1.4 }}>
                                            {item.title}
                                        </Typography>
                                        <Typography color="text.secondary" sx={{ fontSize: '14px', lineHeight: 1.75, flexGrow: 1 }}>
                                            {item.body}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            <WaveDivider top="white" bottom={DARK_800} />
            {/* ── Outcomes ─────────────────────────────────────────────────────── */}
            <Box sx={{ ...SECTION_DARK, color: 'rgba(255,255,255,0.55)', py: { xs: 6, md: 10 } }}>
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={5}>
                            <Chip label="Measurable Results" size="small" sx={{
                                mb: 2, backgroundColor: `${COLOR_PRIMARY}20`, color: '#856400',
                                border: `1px solid ${COLOR_PRIMARY}60`, fontWeight: 700,
                            }} />
                            <Typography variant="h2" sx={{ mb: 2, lineHeight: 1.2, color: 'white' }}>
                                What the engagement delivered
                            </Typography>
                            <Typography color="text.secondary" sx={{ lineHeight: 1.75, color: 'rgba(255,255,255,0.6)' }}>
                                From a standing start with no UK entity, no UK client history and no framework memberships, the engagement has built a structured foundation for long-term public sector growth
                            </Typography>
                            <Box sx={{ mt: 4 }}>
                                <FancyGoldButton onClick={() => setDemoOpen(true)} endIcon={<ArrowForwardIcon />}>
                                    Discuss Your Situation
                                </FancyGoldButton>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <Stack spacing={2}>
                                {heroOutcomes.map((o, i) => (
                                    <Box key={i} sx={{
                                        display: 'flex', alignItems: 'flex-start', gap: 2,
                                        p: 2.5,
                                        border: `1px solid ${ACCENT}15`,
                                        borderLeft: `3px solid ${ACCENT}`,
                                        borderRadius: 1.5,
                                        backgroundColor: `${ACCENT}04`,
                                    }}>
                                        <EmojiEventsIcon sx={{ color: COLOR_PRIMARY, fontSize: 36, mt: 0.2, flexShrink: 0 }} />
                                        <Typography sx={{ fontSize: '14px', lineHeight: 1.65 }}>{o}</Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
            <WaveDivider top={DARK_800} bottom="white" inverted />
            {/* ── Is this right for you? ───────────────────────────────────────── */}
            <Box sx={{ backgroundColor: BG_LIGHT, py: { xs: 6, md: 10 } }}>
                <Container maxWidth="lg">
                    <Typography variant="h2" sx={{ textAlign: 'center', mb: 2 }}>
                        Is this right for your business?
                    </Typography>
                    <Typography color="text.secondary" sx={{ textAlign: 'center', mb: 6, maxWidth: 560, mx: 'auto' }}>
                        This programme is well suited to overseas manufacturers and service providers who meet the following profile
                    </Typography>
                    <Grid container spacing={3} justifyContent="center">
                        {qualifierCards.map(({ Icon, label }) => (
                            <Grid item xs={12} sm={6} md={3} key={label}>
                                <Box sx={{
                                    textAlign: 'center', p: 3,
                                    border: '1px solid rgba(0,0,0,0.08)',
                                    borderRadius: 2, height: '100%',
                                    backgroundColor: 'white',
                                }}>
                                    <Box sx={{
                                        width: 52, height: 52, borderRadius: '50%',
                                        backgroundColor: `${ACCENT}10`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        mx: 'auto', mb: 2,
                                    }}>
                                        <Icon sx={{ color: ACCENT, fontSize: 36 }} />
                                    </Box>
                                    <Typography sx={{ fontSize: '14px', lineHeight: 1.65, color: 'text.secondary' }}>
                                        {label}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                    <Box sx={{ textAlign: 'center', mt: 6 }}>
                        <Typography color="text.secondary" sx={{ mb: 3, fontSize: '15px' }}>
                            The right first step is a conversation. There's no commitment we'll just help you gain a clear picture of the opportunity for your specific situation
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                            <FancyGoldButton onClick={() => setDemoOpen(true)} endIcon={<ArrowForwardIcon />}>
                                Book a Discovery Call
                            </FancyGoldButton>
                            <FancyLightButton to="/contactus">
                                Send Us a Message
                            </FancyLightButton>
                        </Stack>
                    </Box>
                </Container>
            </Box>
            <WaveDivider top="white" bottom={DARK_800} />

            <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
        </Box>
    )
}
